import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, createSearchParams, useLocation } from 'react-router-dom';
import classnames from 'classnames';
import _ from 'lodash';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Label,
  FormGroup,
  Form,
  Input,
  Table,
  Row,
  Col,
  Spinner,
  UncontrolledTooltip,
  TabPane,
  CardFooter,
} from 'reactstrap';
import PageTabs from 'components/PageTabs';
import { FixedCenterLoader } from 'components/Loaders';
import TimePeriod, { formatDates } from 'components/TimePeriod';
import { AutoColumns } from 'components/Columns';
import { useArrayState } from 'components/CustomHooks';
import DownloadSimpleTable from 'components/DownloadSimpleTable';
import ReactDatetime from 'react-datetime';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { getPerformanceEnginePortfolioImpact } from 'services/analysis';
import { optionizeValue, optionizeAll, getValue, getDate, getYearMonth, monthDiff } from 'helpers/select';
import { confidenceLevelToInterval } from 'helpers/formatter';
import { toDeci, toPerc, toPercTable, toDeciTable, tablePresets, getDataType, formatCurrency } from 'helpers/formatter';
import PieChart from 'components/PieChart';
import TightTable from 'components/TightTable';
import { Descriptions, Notes } from '../others';
import AssetSelect from '../AssetSelect';
import Summary from './benchmark-analysis/Summary';
import RiskMetrics from './benchmark-analysis/RiskMetrics';
import AdvancedDetail from './benchmark-analysis/AdvancedDetail';
import Charts from './benchmark-analysis/Charts';
import MultipleAssetSelect from '../MultipleAssetSelect';
import PortfolioImpactBase from './portfolio-impact/PortfolioImpact';
import AssetHeader from 'views/AssetHeader';
import ToggleContainer from 'components/ToggleContainer';

function PortfolioImpact({ session }) {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({
    product: [],
    database: [],
    portfolio_choice: '',
    portfolio_database: '',
    impact: 10,
  });
  const [result, setResult] = useState(null);

  const fetchData = async () => {
    setLoading(true);

    const [data, err] = await getPerformanceEnginePortfolioImpact(options);

    setResult(data);
    setLoading(false);
  };

  useEffect(() => {
    if (options.product.length === 0) return;
    fetchData();
  }, [options]);

  let main = null;
  if (loading) {
    main = <FixedCenterLoader />;
  } else if (result) {
    main = (
      <>
        <AssetHeader asset="Portfolio Impact" />
        <Descriptions content={result.page_description} />
        <Card>
          <CardBody className="tw-text-lg tw-text-center">
            <div>
              Original Portfolio:
              <span className="tw-italic tw-font-semibold tw-ml-1">{result.original_description}</span>
            </div>
            <div>
              New Portfolio:<span className="tw-italic tw-font-semibold tw-ml-1">{result.new_description}</span>
            </div>
          </CardBody>
        </Card>
        <PortfolioImpactBase data={result.impact_output} params={options} />
        <Notes content={result.page_notes} />
      </>
    );
  }

  const minItems = session?.permissions['pe-portfolio-impact-min-items'] ?? 1;
  const maxItems = session?.permissions['pe-portfolio-impact-max-items'] ?? 3;

  const sideMenu = (
    <MultipleAssetSelect
      session={session}
      onSelect={({ assets, databases, pePortfolioDatabase, pePortfolio, allocation }) => {
        setOptions({
          product: assets,
          database: databases,
          portfolio_database: pePortfolioDatabase,
          portfolio_choice: pePortfolio,
          impact: allocation,
        });
      }}
      title={`SELECT ASSETS (Min ${minItems}, Max ${maxItems})`}
      minItems={minItems}
      maxItems={maxItems}
      itemStep={1}
      enablePePortfolioImpact
      onAllowed={({ pePortfolioDatabase, pePortfolio }) => pePortfolioDatabase && pePortfolio}
    />
  );

  const mainContent = <div key={options.product.join('_')}>{main}</div>;

  return (
    <ToggleContainer
      sideMenu={sideMenu}
      mainContent={options.product.length > 0 && mainContent}
      classNames={{ sideMenu: 'tw-w-[400px]' }}
    />
  );
}

export default PortfolioImpact;
